const d3 = require('d3')
const moment = require('moment');
const { parseDate, capitalizeWords } = require('../utils/formatters.js');
const { wrap } = require( '../utils/shared-functions.js');
const { weightConvert } = require('../utils/converter.js');
const { TRANSLATE_GENERAL_TAG } = require('../consts.js');

const WEIGHT_DECREASE = 'rgb(20 148 9)';
const WEIGHT_INCREASE = '#ED6A4F';
const EMPTY_COLOR = '#000000';
const EXTERNALL_COLOR = '#CF42F0';
const MANUAL_COLOR = '#558AC2';
const margin = { top: 40, right: 30, bottom: 40, left: 50 };
const MAX_WEIGHT = { kg: 250, lb: 550,};
const MIN_WEIGHT = { kg: 20, lb: 80};

exports.createWeightChart = function (rawData, manualData, thresholds, onClick, width, height, weight_unit, translate) {
  weight_unit = weight_unit.toLowerCase()
  let prev_value_device;
  let prev_value_manual;
  const data = rawData.map((d, i) => {
    let value = null;
    let prev_value = null;
    const date = parseDate(moment(d['date']).format('YYYY-MM-DD'));
    const value_device = d['value'];
    const value_manual = manualData[i]['value'];
    if (value_device && value_manual) {
      value = ((value_device + value_manual) / (+!!value_device + !!value_manual)).toFixed(1).replace('.0', '');
    } else if (value_device) {
      value = value_device?.toFixed(1).replace('.0', '');
    } else if (value_manual) {
      value = value_manual?.toFixed(1).replace('.0', '');
    }
    if (prev_value_device && prev_value_manual) {
      prev_value =
        ((prev_value_device + prev_value_manual) / (+!!prev_value_device + !!prev_value_manual));
    } else if (prev_value_device) {
      prev_value = prev_value_device;
    } else if (prev_value_manual) {
      prev_value = prev_value_manual;
    }
    const time = value_manual ? manualData[i]['fulldate'] : d['fulldate'];
    const color = value_manual ? MANUAL_COLOR : EXTERNALL_COLOR;
    const time_formatted = moment(time).utc().format('HH:mm');

    if (value_device) prev_value_device = value_device;
    if (value_manual) prev_value_manual = value_manual;

    let arrowStat;
    if (i == 0 || !prev_value || value == prev_value) {
      arrowStat = 0;
    } else if (value > prev_value) {
      arrowStat = 1;
    } else {
      arrowStat = -1;
    }
    const empty = !value;
    return {
      date: date,
      time: time_formatted,
      arrowStat: arrowStat,
      value: weightConvert(value, weight_unit),
      color,
      empty,
    };
  });

  const maxWeightGreatHalf = data.filter(({ value }) => value > MAX_WEIGHT[weight_unit] / 2)[0];

  const x = d3
    .scaleBand()
    .domain(d3.utcDay.range(data[0].date, +data[data.length - 1].date + 1))
    .range([margin.left, width - margin.right])
    .padding(0.4);

  const y = d3
    .scaleLinear()
    .domain([MIN_WEIGHT[weight_unit], maxWeightGreatHalf ? MAX_WEIGHT[weight_unit] : MAX_WEIGHT[weight_unit] / 2])
    .nice()
    .range([height - margin.bottom, margin.top]);

  const xAxis = (g) =>
    g
      .attr('transform', `translate(0,${height - margin.bottom - 2})`)
      .call(d3.axisBottom(x)
          .tickValues(d3.utcDay.every(1).range(data[0].date, +data[data.length - 1].date + 1))
          .tickFormat((d) => moment(d).format('ddd DD'))
          .tickSize(0)
      )

      .call((g) => g.select('.domain').remove());

  const yAxis = (g) =>
    g
      .attr('transform', `translate(${margin.left - 10},0)`)
      .call(
        d3
          .axisLeft(y)
          .tickValues(d3.scaleLinear().domain(y.domain()).ticks())
          .tickSize(0)
          .tickPadding(10)
      )
      .call((g) =>
        g
          .selectAll('.tick line')
          .clone()
          .attr('stroke-opacity', 0.2)
          .attr('x2', width - margin.left - margin.right)
      )
      .call((g) => g.select('.domain').remove());


  const svg = d3
    .create('svg')
    .attr('width', width)
    .attr('height', height);

  var gradient = svg
    .append('svg:defs')
    .append('svg:linearGradient')
    .attr('id', 'gradient')
    .attr('x1', '0%')
    .attr('y1', '0%')
    .attr('x2', '0%')
    .attr('y2', '100%')
    .attr('spreadMethod', 'pad');

  gradient.append('svg:stop').attr('offset', '0%').attr('stop-color', '#B4F7FF').attr('stop-opacity', 1);
  gradient.append('svg:stop').attr('offset', '100%').attr('stop-color', '#4CB3C0').attr('stop-opacity', 1);
  svg.append('g').call(xAxis).selectAll('.tick text').call(wrap, x.bandwidth());
  svg.append('g').call(yAxis);

  const yAxisLabelDate = svg
    .append('text')
    .attr('x', margin.left - 40)
    .attr('y', margin.top - 20)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.units.${weight_unit}`) : capitalizeWords(weight_unit));

  const xAxisLabelDate = svg
    .append('text')
    .attr('x', 20)
    .attr('y', height - margin.bottom + 20)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.general_words.date`) : 'Date');

  const xAxisLabelDay = svg
    .append('text')
    .attr('x', 20)
    .attr('y', height - margin.bottom + 33)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.general_words.day`) : 'Day');

    const fixLabelXPosition = width / data.length / 3.7
  const g = svg
    .append('g')
    .selectAll('g')
    .data(data)
    .join('g')
    .attr('transform', (d) => `translate(${x(d.date) + fixLabelXPosition},0)`);

  const emptyGroup = g.append('g').attr('visibility', (d) => {
    return d.empty ? 'visible' : 'hidden';
  });
  const emptyBar = emptyGroup
    .append('line')
    .attr('y1', (d) => y(MIN_WEIGHT[weight_unit]))
    .attr('y2', (d) => y(maxWeightGreatHalf ? MAX_WEIGHT[weight_unit] : MAX_WEIGHT[weight_unit] / 2))
    .attr('stroke-width', 36)
    .attr('stroke', EMPTY_COLOR)
    .attr('opacity', 0.07);

  const weightBarGroup = g.append('g').attr('visibility', (d) => {
    return d.value ? 'visible' : 'hidden';
  });

  const weightBar = weightBarGroup
    .append('rect')
    .filter(d => d.value)
    .attr('x', (d, i) => x(i))
    .attr('y', (d) => y(d.value))
    .attr('width', x.bandwidth() / 2)
    .attr('height', (d) => {      
      if (!d.value) return 0;
      return y(MIN_WEIGHT[weight_unit]) - y(d.value);
    })
    .attr('fill', (d) => d.color)
    .attr('transform', `translate(${-x.bandwidth() / 4.5},0)`)
    .attr('stroke', 'none')
    .style("cursor", "pointer")
    .on("click", (e, d) => onClick(d.date));

  const gTriangle = weightBarGroup
    .append('g')
    .filter(d => d.value)
    .attr(
      'transform',
      (d) => `translate(1,${y(d.value) + 10 + (d.arrowStat == 1 ? 2 : 5)}) rotate(${d.arrowStat == 1 ? 0 : 180})`
    );

  const triangle = gTriangle
    .append('path')
    .attr('fill', (d) => (d.arrowStat == 0 ? 'none' : d.arrowStat == 1 ? WEIGHT_INCREASE : WEIGHT_DECREASE))
    .attr('stroke-width', 1)
    .attr('stroke', (d) => d.arrowStat == 0 ? 'none' : '#fff')
    .attr('d', d3.symbol().type(d3.symbols[5]).size(50));

  const arrow = gTriangle
    .append('line')
    .attr('y1', (d) => y(d.value))
    .attr('y2', (d) => y(d.value) + 10)
    .attr('stroke-width', 4)
    .attr('transform', (d) => `translate(0,${-y(d.value)})`)
    .attr('stroke', (d) => (d.arrowStat == 0 ? 'none' : d.arrowStat == 1 ? WEIGHT_INCREASE : WEIGHT_DECREASE));

  const rect = gTriangle
   .append('rect')
   .attr('x',(d)=> -2.5)
   .attr('y',3.1)
   .attr('width', 5)
   .attr('height', 7)
   .attr('stroke-width', 1)
   .attr('fill', 'none')
   .attr('stroke', (d) => (d.arrowStat == 0 ? 'none' : '#fff'))

  const weightValueTextBorder = weightBarGroup
    .append('rect')
    .filter(d => d.value)
    .attr('x', 0)
    .attr('y', (d) => y(d.value) - 20)
    .attr('rx', 1) // how much to round corners - to be transitioned below
    .attr('ry', 1)
    .attr('width', 30)
    .attr('height', 14)
    .attr('stroke-opacity', 1)
    .attr('stroke-width', 1)
    .attr('fill', 'none')
    .attr('transform', `translate(${-30 / 2},0)`)
    .attr('stroke', '#4A7BAD');

  const weightValueText = weightBarGroup
    .append('text')
    .filter(d => d.value)
    .attr('text-anchor', 'middle')
    .attr('dy', (d) => y(d.value) - 9.5)
    .style('font-size', '10px')
    .attr('font-weight', '400')
    .text((d) => d.value)
    .style("cursor", "pointer")
    .on("click", (e, d) => onClick(d.date));

  const gLegend = svg.append('g').attr('transform', (d) => `translate(${width-450},${margin.top - 15})`);

  const weightLegendRect = gLegend
    .append('rect')
    .attr('x', 200)
    .attr('y', 0)
    .attr('width', 10)
    .attr('height', 10)
    .attr('fill', EXTERNALL_COLOR);
  const weightLegendAverageText = gLegend
    .append('text')
    .attr('x', 220)
    .attr('y', 9)
    .style('font-size', '17px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.legends.external_measure`) : 'External measurements');

  const weightManualLegendRect = gLegend
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', 10)
    .attr('height', 10)
    .attr('fill', MANUAL_COLOR);

  const weightManualLegendText = gLegend
    .append('text')
    .attr('x', 20)
    .attr('y', 9)
    .style('font-size', '17px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.legends.manual_measure`) : 'Manual measurements');

  const missingValuesLegendRect = gLegend
    .append('rect')
    .attr('x', -170)
    .attr('y', 0)
    .attr('width', 10)
    .attr('height', 10)
    .attr('opacity', 0.11)
    .attr('fill', '#000000');

  const missingValuesLegendText = gLegend
    .append('text')
    .attr('x', -150)
    .attr('y', 9)
    .style('font-size', '17px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.legends.no_measure`) : 'No measurements');

  return svg.node();
};

